/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Close } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { ArrowChevronLeft } from '@jda/lui-common-icon-library-mui5';
import { LuiCard, LuiSelect } from '@jda/lui-common-component-library-mui5';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Backdrop,
  CircularProgress,
  Alert
} from '@mui/material';

import { useStyles } from './styles';
import NativeUserDetails from './NativeUserDetails';
import createFormValueGetter from './formValueGetter';
import DisplayContent from '../../shared/DisplayContent';
import { getUserDetailsById, fetchIdps } from '../../store/user/users';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import interpolateString from '../../utility/stringInterpolation';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import {
  KEY_NAMES,
  USER_FORM_FIELDS,
  LOCAL_IDP_TYPE,
  NATIVE_IDP_TYPE,
  FEDERATED_USER_FIELDS,
  LOCAL_USER_FIELDS,
  NATIVE_USER_EDIT_FIELDS
} from '../UserManagement/constant';
import { updateDirectoryUserDetails, updateFedaratedUserDetails } from '../../services/user';
import { selectUserFormData } from '../../store/user/saveUser/saveUserSelector';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { SUBJECT_ID_COPIED_TO_CLIPBOARD } from '../../container/Users/constants';
import {
  getAllIdps,
  selectSelectedItem,
  getSelectedItemDetails
} from '../../store/user/userSelector';
import { compareByProperty, getUserIdFromUrl, isValidEmail } from '../../utility/commonUtility';
import { LABELS, MESSAGES, KEYS, SNACKBAR_ALERT_DEFAULT_PROPS, STATUS_CODES, FEDERATED_USER_INFO_MESSAGE } from './constant';
import { resetUserFormData, updateUserFormData, updateUserFormDataField } from '../../store/user/saveUser/saveUserActionCreator';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { getIdpById } from '../../services/setting';

const UserDetails = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const selectGetAllIdps = useSelector(getAllIdps);
  const selectedUser = useSelector(selectSelectedItem);
  const selectedUserDetails = useSelector(getSelectedItemDetails);
  const userFormValues = useSelector(selectUserFormData);
  const appLocation = useSelector(selectAppLocation);

  const formDefaultValues = {
    givenName: '',
    lastName: '',
    displayName: '',
  };

  const [formValues, updateFormValues] = useState<any>(formDefaultValues);
  const [locationIdp, setLocationIdp] = useState<any>();
  const [serverErrorMessage, updateServerErrorMessage] = useState('');
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [backdropOpen, setBackDropOpen] = useState<boolean>(true);

  const userDetailsObj = selectedUserDetails.id ? selectedUserDetails : selectedUser;

  const id = userDetailsObj.id || '';
  const idpSubjectId = userDetailsObj.idpSubjectId || '';

  const idpList = useMemo(
    () => {
      const idps = !appLocation ? [...selectGetAllIdps].sort(compareByProperty()) : locationIdp ? [locationIdp] : [];

      return idps.map(idp => ({ ...idp, key: idp.id, displayName: idp.name }));
    },
    [appLocation, selectGetAllIdps, locationIdp]
  );

  const userIdp = idpList.find((idp: { id: string }) => idp.id === userDetailsObj.idpId);

  const isLocalUser = userIdp?.idpType === LOCAL_IDP_TYPE;
  const isNativeUser = userIdp?.idpType === NATIVE_IDP_TYPE || userIdp?.type === 'NATIVE';

  useEffect(() => {
    if (appLocation && userDetailsObj.idpId) {
      getIdpById(userDetailsObj.idpId).then(setLocationIdp);
    }
  }, [appLocation, userDetailsObj.idpId]);

  useEffect(() => {
    getUserDetails();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeUser]);

  const getUserDetails = async () => {
    if (history.location && history.location.pathname) {
      const userId = await getUserIdFromUrl(history.location.pathname);
      const userData: any = await getUserDetailsById(dispatch, userId, isNativeUser, appLocation?.name);
      updateFormValues(userData);
      Object.keys(userData).forEach((field: any) => {
        updateFormFields(field, userData[field]);
      });
      setBackDropOpen(false);
    }
  };

  const handleCopyClick = () => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: SUBJECT_ID_COPIED_TO_CLIPBOARD
      })
    );
  };

  const idpTypeChangeHandler = (_e: any) => { };

  const IDPNameData = useCallback(() => {
    setErrors({ message: '' });
    fetchIdps(dispatch).catch((_error: any) => {
      const errorMessage = {
        message: MESSAGES.ERROR_IN_FETCHING_IDENTITY_PROVIDER_NAME
      };
      setErrors(errorMessage);
    });
  }, [dispatch]);

  const getSelectedItem = (value: string, key: string, itemList: any) => {
    const selItem = itemList.filter((item: any) => {
      return item[key] === value;
    });
    return Array.isArray(selItem) && selItem.length > 0 ? selItem[0] : '';
  };

  const updateFormFields = (fieldName: string, value: any) => {
    updateFormValues({ ...formValues, [fieldName]: value });
    dispatch(updateUserFormDataField(fieldName, { value, error: false, errorHelperText: '' }));
  };

  const getFormFieldValue = useMemo(() => createFormValueGetter(userFormValues), [userFormValues]);

  const changedFields = useMemo(() => {
    const isValueModified = (fieldName: string) => {
      const formFieldValue = getFormFieldValue(fieldName);
      let valueChanged = false;
      if (formFieldValue !== userDetailsObj[fieldName]) {
        if (!(formFieldValue === '' && !userDetailsObj[fieldName])) {
          valueChanged = true;
        }
      }
      return valueChanged;
    };

    const fields = isNativeUser ? NATIVE_USER_EDIT_FIELDS : isLocalUser ? LOCAL_USER_FIELDS : FEDERATED_USER_FIELDS;

    return fields.filter(isValueModified);
  }, [getFormFieldValue, userDetailsObj, isLocalUser, isNativeUser]);

  const validateRequired = () => {
    let formValid = true;
    let newData = JSON.parse(JSON.stringify(userFormValues));

    // if (isNativeUser && getFormFieldValue(USER_FORM_FIELDS.GIVEN_NAME) === '') {
    //   newData[USER_FORM_FIELDS.GIVEN_NAME].error = true;
    //   newData[USER_FORM_FIELDS.GIVEN_NAME].errorHelperText = MESSAGES.FIRST_NAME_REQUIRED;
    //   formValid = false;
    // }

    // if (isNativeUser && getFormFieldValue(USER_FORM_FIELDS.LAST_NAME) === '') {
    //   newData[USER_FORM_FIELDS.LAST_NAME].error = true;
    //   newData[USER_FORM_FIELDS.LAST_NAME].errorHelperText = MESSAGES.LAST_NAME_REQUIRED;
    //   formValid = false;
    // }

    const emailValue = getFormFieldValue(USER_FORM_FIELDS.EMAIL) as string;
    if (isLocalUser && emailValue === '') {
      newData[USER_FORM_FIELDS.EMAIL].error = true;
      newData[USER_FORM_FIELDS.EMAIL].errorHelperText = MESSAGES.EMAIL_REQUIRED;
      formValid = false;
    } else if (emailValue !== '' && !isValidEmail(emailValue)) {
      newData[USER_FORM_FIELDS.EMAIL].error = true;
      newData[USER_FORM_FIELDS.EMAIL].errorHelperText = MESSAGES.EMAIL_INVALID;
      formValid = false;
    }
    if (isLocalUser && getFormFieldValue(USER_FORM_FIELDS.DISPLAY_NAME) === '') {
      newData[USER_FORM_FIELDS.DISPLAY_NAME].error = true;
      newData[USER_FORM_FIELDS.DISPLAY_NAME].errorHelperText = MESSAGES.DISPLAY_NAME_REQUIRED;
      formValid = false;
    }

    if (isLocalUser) {
      const isMfaEnabled = getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) === true;
      const preferredMfa = getFormFieldValue(USER_FORM_FIELDS.PREFERRED_MFA);
      const mobilePhoneValue = getFormFieldValue(USER_FORM_FIELDS.MOBILE_PHONE) as string;

      if (mobilePhoneValue) {
        if (!isValidPhoneNumber(mobilePhoneValue)) {
          newData[USER_FORM_FIELDS.MOBILE_PHONE].error = true;
          newData[USER_FORM_FIELDS.MOBILE_PHONE].errorHelperText = MESSAGES.MOBILE_NUMBER_INVALID;
          formValid = false;
        }
      } else if (isMfaEnabled === true && preferredMfa === KEY_NAMES.PREFERRED_MFA_PHONE) {
        newData[USER_FORM_FIELDS.MOBILE_PHONE].error = true;
        newData[USER_FORM_FIELDS.MOBILE_PHONE].errorHelperText = MESSAGES.MOBILE_NUMBER_REQUIRED;
        formValid = false;
      }

      if (isMfaEnabled === true && preferredMfa === '') {
        newData[USER_FORM_FIELDS.PREFERRED_MFA].error = true;
        newData[USER_FORM_FIELDS.PREFERRED_MFA].errorHelperText = MESSAGES.PREFERRED_MFA_REQUIRED;
        formValid = false;
      }
    }

    if (!formValid) {
      dispatch(updateUserFormData(newData));
    }

    return formValid;
  };

  const displayServerErrorMessage = () => {
    let serverErrorElement;
    if (serverErrorMessage !== '') {
      const action = (
        <IconButton
          color="inherit"
          aria-label="Close"
          data-testid="remove-server-alert"
          onClick={() => updateServerErrorMessage('')}
        >
          <Close fontSize="small" />
        </IconButton>
      );
      serverErrorElement = (
        <Alert
          data-testid="server-error-display"
          severity="error"
          action={action}
          className={classes.serverErrorMessage}
        >
          {serverErrorMessage}
        </Alert>
      );
    }
    return serverErrorElement;
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };

  const saveUserDetails = () => {
    updateServerErrorMessage('');
    setBackDropOpen(true);
    if (validateRequired()) {
      const payload = Object.fromEntries(changedFields.map(field => [field, getFormFieldValue(field)]));
      updateDirectoryUserDetails(id, payload).then(() => {
        displaySnackbar(interpolateString([userDetailsObj.userName], MESSAGES.USER_UPDATE_SUCCESS), 'success');
        getUserDetailsById(dispatch, id, isNativeUser, appLocation?.name);
      }).catch((_error: any) => {
        let errorMessage =
          typeof _error.message === 'string'
            ? `${MESSAGES.USER_UPDATE_ERROR} ${_error.message}`
            : MESSAGES.USER_UPDATE_ERROR;
        if (
          _error.response &&
          (_error.response.status === STATUS_CODES.ERROR_400 || _error.response.status === STATUS_CODES.ERROR_500)
        ) {
          if (_error.response.data && _error.response.data.message)
            errorMessage = `${MESSAGES.USER_UPDATE_ERROR} ${_error.response.data.message}`;
        }
        updateServerErrorMessage(errorMessage);
      }).finally(() => {
        setBackDropOpen(false);
      });
    } else {
      setBackDropOpen(false);
    }
  };

  useEffect(() => {
    IDPNameData();
  }, [IDPNameData]);

  useEffect(() => {
    return () => {
      dispatch(resetUserFormData());
    };
  }, [dispatch]);

  if (!userDetailsObj.id) return null;
  const onClickBackBtn = () => {
    if (history.location.search) {
      history.push(`/${history.location.search}`);
    } else {
      history.push('/');
    }
  };

  return (
    <Box className={classes.root}>
      <AppBar elevation={0} position="static" className={classes.appBarWrap}>
        <Tooltip arrow title={'Back'} placement="bottom" >
          <IconButton
            className={classes.backIcon}
            data-testid="api-header-back-btn"
            onClick={onClickBackBtn}
          >
            <ArrowChevronLeft />
          </IconButton>
        </Tooltip>
        <Box className={classes.headingAlignment}>{userDetailsObj.userName}</Box>
      </AppBar>
      <Backdrop open={backdropOpen}>
        <CircularProgress color="inherit" size={44} />
      </Backdrop>
      <LuiCard
        title={LABELS.USER_DETAIL}
        titleVariant="h2"
        hasCardContentPadding={true}
        footerActions={(
          <Box className={classes.footerActionsWrap}>
            <Button
              size="medium"
              color="primary"
              variant="outlined"
              data-testid="cancel-btn"
              className={classes.cancelBtn}
              onClick={onClickBackBtn}
            >
              {LABELS.BUTTON_CANCEL}
            </Button>
            <Button
              size="medium"
              color="primary"
              variant="contained"
              data-testid="save-btn"
              onClick={saveUserDetails}
              disabled={changedFields.length === 0}
              className={classes.saveSettingsBtn}
            >
              {LABELS.BUTTON_SAVE}
            </Button>
          </Box>
        )}
      >
        {displayServerErrorMessage()}
        <Box className={classes.userDetailWrap} height={height - 32 * 8}>
          <Box className={classes.userDetailRightWrap}>
            {(isLocalUser || isNativeUser) && (
              <NativeUserDetails
                id={userDetailsObj.id}
                handleCopyClick={handleCopyClick}
                userName={userDetailsObj.userName}
              />
            )}
            {!(isLocalUser || isNativeUser) && (
              <Box>
                <Box className={classes.warningInfoWrap}>
                  <Alert
                    elevation={0}
                    severity={'info'}
                    variant={'outlined'}
                  >
                    {FEDERATED_USER_INFO_MESSAGE}
                  </Alert>
                </Box>
                <Grid container spacing={4} className={classes.gridViewWrap}>
                  <Grid item xs={4} className={classes.contentContainer}>
                    <Box className={classes.dropDownText}>
                      <LuiSelect
                        required
                        disabled
                        maxHeight={350}
                        id={USER_FORM_FIELDS.IDP_ID}
                        data-testid={USER_FORM_FIELDS.IDP_ID}
                        inputLabel={LABELS.IDENTITY_PROVIDER}
                        onSelectionChange={idpTypeChangeHandler}
                        itemData={idpList}
                        selectedItem={getSelectedItem(userDetailsObj.idpId, KEY_NAMES.ID, idpList)}
                      />
                    </Box>
                    {errors && <ErrorMessage {...errors} />}
                  </Grid>
                  <Grid item xs={4} className={classes.contentContainer}>
                    <TextField
                      fullWidth
                      id="firstName"
                      variant="standard"
                      name="firstName"
                      label={LABELS.FIRST_NAME}
                      data-testid={"first-name"}
                      error={userFormValues.givenName.error}
                      className={classes.textInput}
                      value={userFormValues.givenName.value}
                      defaultValue={userDetailsObj.givenName}
                      helperText={userFormValues.givenName.errorHelperText}
                      onChange={(e: any) => updateFormFields('givenName', e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={4} className={classes.contentContainer}>
                    <TextField
                      fullWidth
                      id="lastName"
                      name="lastName"
                      label={LABELS.LAST_NAME}
                      variant="standard"
                      data-testid={"last-name"}
                      error={userFormValues.lastName.error}
                      className={classes.textInput}
                      inputProps={{ maxLength: 100 }}
                      value={userFormValues.lastName.value}
                      defaultValue={userDetailsObj.lastName}
                      helperText={userFormValues.lastName.errorHelperText}
                      onChange={(e: any) => updateFormFields('lastName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4} className={classes.contentContainer}>
                    <TextField
                      required
                      fullWidth
                      variant="standard"
                      id="displayName"
                      name="displayName"
                      label={LABELS.DISPLAY_NAME}
                      data-testid={"display-name"}
                      error={userFormValues.displayName.error}
                      className={classes.textInput}
                      inputProps={{ maxLength: 100 }}
                      value={userFormValues.displayName.value}
                      defaultValue={userDetailsObj.displayName}
                      helperText={userFormValues.displayName.errorHelperText}
                      onChange={(e: any) => updateFormFields('displayName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Box className={classes.subjectIdWrap}>
                      <DisplayContent
                        contentText={LABELS.SUBJECT_ID}
                        content={idpSubjectId}
                        testId={KEYS.SUBJECT_ID}
                        isNewUser={idpSubjectId === '' ? true : false}
                        handleCopyClick={handleCopyClick}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box className={classes.divider}>
                    <Divider />
                  </Box>
                </Grid>
                <Box className={classes.tabHeader}>{LABELS.SIGN_IN_DETAILS}</Box>
                <Grid item xs={4}>
                  <Box className={classes.contentContainer}>
                    <TextField
                      required
                      fullWidth
                      id="userName"
                      name="userName"
                      variant="standard"
                      disabled={true}
                      label={LABELS.USER_NAME}
                      data-testid={"user-name"}
                      className={classes.textInputSignIn}
                      inputProps={{ maxLength: 100 }}
                      value={userFormValues.userName.value}
                      defaultValue={userDetailsObj.userName}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className={classes.divider}>
                    <Divider />
                  </Box>
                </Grid>
                <Box className={classes.tabHeader}>{LABELS.CONTACT_DETAILS}</Box>
                <Grid item xs={4}>
                  <Box className={classes.contentContainer}>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      variant="standard"
                      data-testid={"email"}
                      label={LABELS.EMAIL}
                      error={userFormValues.email.error}
                      className={classes.textInputSignIn}
                      inputProps={{ maxLength: 100 }}
                      value={userFormValues.email.value}
                      defaultValue={userDetailsObj.email}
                      helperText={userFormValues.email.errorHelperText}
                      onChange={(e: any) => updateFormFields('email', e.target.value)}
                    />
                  </Box>
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </LuiCard>
      <SnackbarAlert />
    </Box>
  );
};

export default UserDetails;
